import { type HTMLAttributes, type ReactNode } from "react";
import { AlertTriangle, ExternalLink, Info, OctagonAlert, RefreshCw } from "lucide-react";
import { cn } from "@/lib/cn.js";

export type BannerKind =
  | "sync_conflict"
  | "routing_overridden"
  | "external_state_stale_at_booking"
  | "pending_reschedule"
  | "info";

export interface BannerProps extends HTMLAttributes<HTMLDivElement> {
  kind: BannerKind;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}

const ICONS: Record<BannerKind, React.ComponentType<{ className?: string }>> = {
  sync_conflict: OctagonAlert,
  routing_overridden: AlertTriangle,
  external_state_stale_at_booking: RefreshCw,
  pending_reschedule: ExternalLink,
  info: Info
};

const TONES: Record<BannerKind, string> = {
  sync_conflict: "border-destructive/30 bg-destructive/8 text-destructive [--banner-icon:hsl(var(--destructive))]",
  routing_overridden: "border-warning/30 bg-warning/8 text-foreground [--banner-icon:hsl(var(--warning))]",
  external_state_stale_at_booking: "border-warning/30 bg-warning/8 text-foreground [--banner-icon:hsl(var(--warning))]",
  pending_reschedule: "border-info/30 bg-info/8 text-foreground [--banner-icon:hsl(var(--info))]",
  info: "border-border bg-muted/60 text-foreground [--banner-icon:hsl(var(--muted-foreground))]"
};

export function Banner({ kind, title, description, action, className, ...props }: BannerProps): React.JSX.Element {
  const Icon = ICONS[kind];
  return (
    <div
      role="status"
      className={cn(
        "flex flex-col gap-2 rounded-md border px-3 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between",
        TONES[kind],
        className
      )}
      data-banner-kind={kind}
      {...props}
    >
      <div className="flex items-start gap-2.5">
        <span style={{ color: "var(--banner-icon)" }} className="mt-0.5 shrink-0">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <div className="font-medium leading-snug">{title}</div>
          {description && <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
